import PageControl from "../components/page_control/PageControl.vue";
import PageComponent from "../components/page/PageComponent.vue";
import SectionSheet from "../components/section_sheet/SectionSheet.vue";
import { format_date } from "@/utils/date_formatter";

export default {
  name: "AddressVerificationReport",
  components: { PageControl, PageComponent, SectionSheet },
  props: {
    sheet_elements: {
      type: String,
      default: ".sheet",
      required: false,
    },
    address_data: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      export_options: {
        consumer_details: false,
        address_history: false,
        address_analysis: false,
        raw_json: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
      },
    };
  },
  computed: {
    safe_address_data() {
      return this.address_data || {};
    },

    // Consumer Data
    consumer_data() {
      return this.safe_address_data.Consumer?.ConsumerDetail || {};
    },

    // Report ID
    report_id() {
      return (
        this.safe_address_data.Consumer?.ReportInformation?.ReportID || "N/A"
      );
    },

    // Report metadata
    report_reference() {
      return `AVR-${this.report_id}`;
    },

    generated_date() {
      return new Date().toLocaleDateString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },

    total_pages() {
      return 4;
    },

    // Consumer Information
    consumer_id() {
      return this.consumer_data.ConsumerID || "N/A";
    },

    consumer_full_name() {
      const names = [
        this.consumer_data.FirstName,
        this.consumer_data.SecondName,
        this.consumer_data.ThirdName,
        this.consumer_data.Surname,
      ].filter((name) => name && name.trim() !== "");
      return names.join(" ") || "N/A";
    },

    consumer_id_number() {
      return this.consumer_data.IDNo || "N/A";
    },

    consumer_gender() {
      return this.consumer_data.Gender || "N/A";
    },

    consumer_marital_status() {
      return this.consumer_data.MaritalStatusDesc || "N/A";
    },

    formatted_birth_date() {
      if (!this.consumer_data.BirthDate) return "N/A";
      return this.formatDate(this.consumer_data.BirthDate);
    },

    consumer_home_phone() {
      return this.consumer_data.HomeTelephoneNo;
    },

    consumer_work_phone() {
      return this.consumer_data.WorkTelephoneNo;
    },

    consumer_mobile_phone() {
      return this.consumer_data.CellularNo;
    },

    consumer_email() {
      return this.consumer_data.EmailAddress;
    },

    consumer_employer() {
      return this.consumer_data.EmployerDetail;
    },

    consumer_privacy_status() {
      return this.consumer_data.PrivacyStatus || "N/A";
    },

    current_residential_address() {
      return this.consumer_data.ResidentialAddress || "N/A";
    },

    current_postal_address() {
      return this.consumer_data.PostalAddress || "N/A";
    },

    // Address History Data
    address_history() {
      return this.safe_address_data.Consumer?.ConsumerAddressHistory || [];
    },

    // Pagination for address history
    paginated_address_history() {
      const pages = [];
      for (let i = 0; i < this.address_history.length; i += 20) {
        pages.push(this.address_history.slice(i, i + 20));
      }
      return pages;
    },

    total_addresses() {
      return this.address_history.length;
    },

    residential_addresses() {
      return this.address_history.filter(
        (addr) => addr.AddressType === "Residential"
      );
    },

    postal_addresses() {
      return this.address_history.filter(
        (addr) => addr.AddressType === "Postal"
      );
    },

    residential_address_count() {
      return this.residential_addresses.length;
    },

    postal_address_count() {
      return this.postal_addresses.length;
    },

    residential_address_percentage() {
      if (this.total_addresses === 0) return 0;
      return Math.round(
        (this.residential_address_count / this.total_addresses) * 100
      );
    },

    postal_address_percentage() {
      if (this.total_addresses === 0) return 0;
      return Math.round(
        (this.postal_address_count / this.total_addresses) * 100
      );
    },

    // Unique address analysis
    unique_addresses() {
      const unique_set = new Set();
      this.address_history.forEach((addr) => {
        const normalized_address = this.normalize_address(addr.Address);
        unique_set.add(normalized_address);
      });
      return Array.from(unique_set);
    },

    unique_address_count() {
      return this.unique_addresses.length;
    },

    // Date analysis
    earliest_report_date() {
      if (this.address_history.length === 0) return "N/A";
      const dates = this.address_history
        .map((addr) => new Date(addr.FirstReportedDate))
        .sort((a, b) => a - b);
      return this.formatDate(dates[0]);
    },

    most_recent_update_date() {
      if (this.address_history.length === 0) return "N/A";
      const dates = this.address_history
        .map((addr) => new Date(addr.LastUpdatedDate))
        .sort((a, b) => b - a);
      return this.formatDate(dates[0]);
    },

    address_date_range() {
      if (this.address_history.length === 0) return "N/A";
      const start_date = this.earliest_report_date;
      const end_date = this.most_recent_update_date;
      return `${start_date} - ${end_date}`;
    },

    address_history_span() {
      if (this.address_history.length === 0) return "N/A";
      const earliest = new Date(
        this.address_history.reduce((earliest, addr) => {
          return new Date(addr.FirstReportedDate) <
            new Date(earliest.FirstReportedDate)
            ? addr
            : earliest;
        }).FirstReportedDate
      );
      const latest = new Date(
        this.address_history.reduce((latest, addr) => {
          return new Date(addr.LastUpdatedDate) >
            new Date(latest.LastUpdatedDate)
            ? addr
            : latest;
        }).LastUpdatedDate
      );

      const years = Math.floor(
        (latest - earliest) / (365.25 * 24 * 60 * 60 * 1000)
      );
      const months = Math.floor(
        ((latest - earliest) % (365.25 * 24 * 60 * 60 * 1000)) /
          (30.44 * 24 * 60 * 60 * 1000)
      );

      if (years > 0) {
        return `${years} year${years > 1 ? "s" : ""} ${months} month${
          months > 1 ? "s" : ""
        }`;
      }
      return `${months} month${months > 1 ? "s" : ""}`;
    },

    current_address_age() {
      if (this.address_history.length === 0) return "N/A";
      const most_recent = this.address_history.reduce((latest, addr) => {
        return new Date(addr.LastUpdatedDate) > new Date(latest.LastUpdatedDate)
          ? addr
          : latest;
      });

      const first_reported = new Date(most_recent.FirstReportedDate);
      const now = new Date();
      const months = Math.floor(
        (now - first_reported) / (30.44 * 24 * 60 * 60 * 1000)
      );
      const years = Math.floor(months / 12);
      const remaining_months = months % 12;

      if (years > 0) {
        return `${years} year${years > 1 ? "s" : ""} ${remaining_months} month${
          remaining_months > 1 ? "s" : ""
        }`;
      }
      return `${months} month${months > 1 ? "s" : ""}`;
    },

    current_address_type() {
      if (this.address_history.length === 0) return "N/A";
      const most_recent = this.address_history.reduce((latest, addr) => {
        return new Date(addr.LastUpdatedDate) > new Date(latest.LastUpdatedDate)
          ? addr
          : latest;
      });
      return most_recent.AddressType;
    },

    // Postal code analysis
    postal_code_distribution() {
      const postal_codes = {};
      this.address_history.forEach((addr) => {
        const code = addr.PostalCode || "Unknown";
        postal_codes[code] = (postal_codes[code] || 0) + 1;
      });

      return Object.entries(postal_codes)
        .map(([code, count]) => ({
          code,
          count,
          percentage: Math.round((count / this.total_addresses) * 100),
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5); // Top 5 postal codes
    },

    most_common_postal_code() {
      if (this.postal_code_distribution.length === 0) return "N/A";
      return this.postal_code_distribution[0].code;
    },

    // Address type distribution
    address_type_distribution() {
      const types = {};
      this.address_history.forEach((addr) => {
        const type = addr.AddressType || "Unknown";
        types[type] = (types[type] || 0) + 1;
      });

      return Object.entries(types)
        .map(([type, count]) => ({
          type,
          count,
          percentage: Math.round((count / this.total_addresses) * 100),
        }))
        .sort((a, b) => b.count - a.count);
    },

    total_address_updates() {
      return this.address_history.reduce((total, addr) => {
        const first_reported = new Date(addr.FirstReportedDate);
        const last_updated = new Date(addr.LastUpdatedDate);
        return (
          total + (first_reported.getTime() !== last_updated.getTime() ? 1 : 0)
        );
      }, 0);
    },

    average_address_duration() {
      if (this.address_history.length === 0) return "N/A";

      let total_duration = 0;
      let count = 0;

      this.address_history.forEach((addr) => {
        const first_reported = new Date(addr.FirstReportedDate);
        const last_updated = new Date(addr.LastUpdatedDate);
        const duration =
          (last_updated - first_reported) / (1000 * 60 * 60 * 24); // days
        total_duration += duration;
        count++;
      });

      const average_days = total_duration / count;
      const months = Math.round(average_days / 30.44);

      return `${months} month${months > 1 ? "s" : ""}`;
    },

    // Subscriber information
    subscriber_name() {
      return (
        this.safe_address_data.Consumer?.SubscriberInputDetails
          ?.SubscriberName || "N/A"
      );
    },

    // Export Options
    has_export_options_selected() {
      return Object.values(this.export_options).some((option) => option);
    },

    modal_instance() {
      const modal_element = document.getElementById(
        `export-modal-${this.report_reference}`
      );
      const modal = new window.bootstrap.Modal(modal_element);
      return modal;
    },
  },

  methods: {
    // Utility methods
    formatDate(date) {
      return format_date(date);
    },

    normalize_address(address) {
      // Accepts either a string or an address object
      if (!address) return "";
      if (typeof address === "string") {
        return address.replace(/\s+/g, " ").trim().toUpperCase();
      }
      if (typeof address === "object" && address.Address) {
        return address.Address.replace(/\s+/g, " ").trim().toUpperCase();
      }
      return "";
    },

    get_address_type_class(address_type) {
      const type = address_type.toLowerCase();
      if (type === "residential") return "residential";
      if (type === "postal") return "postal";
      return "other";
    },

    get_address_type_icon(address_type) {
      const type = address_type.toLowerCase();
      if (type === "residential") return "bi bi-house-door";
      if (type === "postal") return "bi bi-mailbox";
      return "bi bi-geo-alt";
    },

    // Export functionality
    show_export_modal() {
      this.modal_instance.show();
    },
    hide_export_modal() {
      this.modal_instance.hide();
    },
    async load_export_libraries() {
      try {
        const promises = [];

        if (!this.libraries_loaded.papa_parse) {
          promises.push(this.load_papa_parse());
        }

        if (!this.libraries_loaded.sheetjs) {
          promises.push(this.load_sheetjs());
        }

        await Promise.all(promises);
        return true;
      } catch (error) {
        console.error("Failed to load export libraries:", error);
        throw new Error("Failed to load required libraries for export");
      }
    },

    async load_papa_parse() {
      return new Promise((resolve, reject) => {
        if (window.Papa) {
          this.libraries_loaded.papa_parse = true;
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src =
          "https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.4.1/papaparse.min.js";
        script.onload = () => {
          this.libraries_loaded.papa_parse = true;
          resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    async load_sheetjs() {
      return new Promise((resolve, reject) => {
        if (window.XLSX) {
          this.libraries_loaded.sheetjs = true;
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src =
          "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
        script.onload = () => {
          this.libraries_loaded.sheetjs = true;
          resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    async export_data() {
      try {
        await this.ensure_libraries_loaded();

        const export_data = this.prepare_export_data();

        if (this.export_format === "csv") {
          this.export_as_csv(export_data);
        } else if (this.export_format === "excel") {
          this.export_as_excel(export_data);
        } else if (this.export_format === "json") {
          this.export_as_json(export_data);
        }

        this.hide_export_modal();
      } catch (error) {
        console.error("Export failed:", error);
        alert("Export failed. Please try again.");
      }
    },
    prepare_export_data() {
      const export_data = {};

      if (this.export_options.consumer_details) {
        export_data.consumer_details = {
          consumer_id: this.consumer_id,
          full_name: this.consumer_full_name,
          id_number: this.consumer_id_number,
          birth_date: this.formatted_birth_date,
          gender: this.consumer_gender,
          marital_status: this.consumer_marital_status,
          home_phone: this.consumer_home_phone,
          work_phone: this.consumer_work_phone,
          mobile_phone: this.consumer_mobile_phone,
          email: this.consumer_email,
          employer: this.consumer_employer,
          privacy_status: this.consumer_privacy_status,
          residential_address: this.current_residential_address,
          postal_address: this.current_postal_address,
        };
      }

      if (this.export_options.address_history) {
        export_data.address_history = this.address_history.map((addr) => ({
          address_id: addr.ConsumerAddressID,
          address_type: addr.AddressType,
          address: addr.Address,
          postal_code: addr.PostalCode,
          first_reported: addr.FirstReportedDate,
          last_updated: addr.LastUpdatedDate,
        }));
      }

      if (this.export_options.address_analysis) {
        export_data.address_analysis = {
          total_addresses: this.total_addresses,
          residential_count: this.residential_address_count,
          postal_count: this.postal_address_count,
          unique_addresses: this.unique_address_count,
          most_common_postal_code: this.most_common_postal_code,
          address_history_span: this.address_history_span,
          current_address_age: this.current_address_age,
          postal_code_distribution: this.postal_code_distribution,
          address_type_distribution: this.address_type_distribution,
        };
      }

      if (this.export_options.raw_json) {
        export_data.raw_data = {
          report_metadata: {
            report_reference: this.report_reference,
            generated_date: this.generated_date,
            export_timestamp: new Date().toISOString(),
          },
          address_data: this.safe_address_data,
        };
      }

      return export_data;
    },
    export_as_csv(export_data) {
      const Papa = window.Papa;
      const current_date = this.get_current_date_string();
      const tables_to_export = [];

      // Add headers and data for each selected section
      Object.keys(export_data).forEach((section) => {
        const data = export_data[section];
        let csv_content;

        if (Array.isArray(data)) {
          csv_content = Papa.unparse(data);
        } else {
          csv_content = Papa.unparse([data]);
        }

        tables_to_export.push({
          name: `address_verification_${this.report_reference}_${section}_${current_date}.csv`,
          content: csv_content,
          type: "text/csv",
        });
      });

      // If there's only one table, download it directly
      if (tables_to_export.length === 1) {
        const table = tables_to_export[0];
        this.download_file(table.content, table.name, table.type);
      } else {
        // If multiple tables, create a zip file (for future implementation)
        // For now, we'll download them individually
        tables_to_export.forEach((table) => {
          this.download_file(table.content, table.name, table.type);
        });
      }
    },
    export_as_excel(export_data) {
      try {
        const XLSX = window.XLSX;
        const current_date = this.get_current_date_string();
        const workbook = XLSX.utils.book_new();

        Object.keys(export_data).forEach((section) => {
          let sheetName = section.substring(0, 31).replace(/[:\\/?*[\]]/g, "_");
          const data = export_data[section];
          let worksheet;

          if (Array.isArray(data)) {
            worksheet = XLSX.utils.json_to_sheet(data);
          } else {
            worksheet = XLSX.utils.json_to_sheet([data]);
          }

          XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        });

        // For raw JSON data, format it better in Excel
        if (export_data.raw_data) {
          // Create a simpler version for Excel display
          const json_table = [
            {
              "JSON Data": JSON.stringify(export_data.raw_data, null, 2),
            },
          ];
          const worksheet = XLSX.utils.json_to_sheet(json_table);
          XLSX.utils.book_append_sheet(workbook, worksheet, "Raw JSON Data");
        }

        const excel_buffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });
        const blob = new Blob([excel_buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        this.download_blob(
          blob,
          `address_verification_${this.report_reference}_${current_date}.xlsx`
        );
      } catch (err) {
        console.error("Excel export error:", err);
        throw err; // rethrow so export_data's catch will show the alert
      }
    },
    export_as_json(export_data) {
      const current_date = this.get_current_date_string();

      // Add metadata to the JSON export
      const enhanced_data = {
        report_metadata: {
          report_reference: this.report_reference,
          generated_date: this.generated_date,
          export_timestamp: new Date().toISOString(),
        },
        ...export_data,
      };

      const json_string = JSON.stringify(enhanced_data, null, 2);
      const filename = `address_verification_${this.report_reference}_${current_date}.json`;
      this.download_file(json_string, filename, "application/json");
    },

    download_file(content, filename, content_type) {
      const blob = new Blob([content], { type: content_type });
      this.download_blob(blob, filename);
    },

    download_blob(blob, filename) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },

    get_current_date_string() {
      const current_date = new Date();
      return current_date.toISOString().split("T")[0];
    },

    async ensure_libraries_loaded() {
      try {
        // Check if libraries are already loaded
        if (this.libraries_loaded.papa_parse && this.libraries_loaded.sheetjs) {
          return true;
        }

        // Otherwise load them
        await this.load_export_libraries();
        return true;
      } catch (error) {
        console.error("Error loading libraries:", error);
        throw error;
      }
    },
  },
};
